import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'

import { SparkTokenNavLinkContent } from './components/SparkTokenNavLinkContent'
import { LINKS_DATA } from './constants'
import { TopbarButton } from './TopbarButton'
import { TopbarNavigationProps } from './TopbarNavigation'
import { formatPercentage } from '@/domain/common/format'
import { SavingsAPYBadge } from '@/features/savings/components/navbar-item/SavingsAPYBadge'
import { assets } from '@/ui/assets'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/atoms/accordion/Accordion'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { IconButton } from '@/ui/atoms/icon-button/IconButton'
import { InternalLink } from '@/ui/atoms/link/Link'
import { MenuItem } from '@/ui/atoms/menu-item/MenuItem'
import { cn } from '@/ui/utils/style'

export function TopbarNavigationDialog({
  savingsConverter,
  blockedPages,
  sparkLendSubLinks,
  spkTokenSubLinks,
  isSparkLendSubLinkActive,
  isSparkTokenSubLinkActive,
}: TopbarNavigationProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  function closeDialog() {
    setDialogOpen(false)
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <IconButton variant="transparent" size="m" icon={ChevronDownIcon} className="w-10 sm:w-14" />
      </DialogTrigger>

      <DialogContent overlayVariant="default" contentVerticalPosition="bottom" spacing="none">
        <DialogTitle className="border-primary border-b p-5 pt-6">
          <img src={assets.brand.logoDark} alt="Spark logo" className="h-6" />
        </DialogTitle>

        {!blockedPages.some((page) => page === '/' || page === '/savings/$domain/$savingsToken') && ( // some instead of includes for better type inference
          <TopbarButton
            to="/"
            activeRoutes={['/savings/$domain/$savingsToken']}
            label={LINKS_DATA.savings.label}
            prefixIcon={LINKS_DATA.savings.icon}
            type="savings"
            postfixSlot={
              savingsConverter?.data || savingsConverter?.isLoading ? (
                <SavingsAPYBadge APY={savingsConverter.data?.apy} isLoading={savingsConverter.isLoading} />
              ) : undefined
            }
            onClick={closeDialog}
          />
        )}

        <div className="border-primary border-t" />

        <Accordion type="single" collapsible defaultValue={isSparkLendSubLinkActive ? 'nav-accordion' : ''}>
          <AccordionItem value="nav-accordion">
            <AccordionTrigger className="w-full p-0 pr-5" role="none">
              <TopbarButton
                label={LINKS_DATA.sparkLend.label}
                type="sparkLend"
                prefixIcon={LINKS_DATA.sparkLend.icon}
                active={isSparkLendSubLinkActive}
                className="w-full"
              />
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-2 px-12 pb-4">
              {sparkLendSubLinks.map(({ link, label }) => (
                <InternalLink key={link.to} {...link}>
                  {({ isActive }) => (
                    <MenuItem
                      className={cn('overflow-hidden border border-primary text-secondary', isActive && 'text-primary')}
                      onClick={closeDialog}
                    >
                      <div
                        className={cn(
                          // @note: one time use gradient
                          'absolute top-0 bottom-0 left-0 hidden w-1 bg-linear-to-br from-[#FFCD4D] to-[#FF895D] opacity-0',
                          isActive && 'block opacity-100',
                        )}
                      />
                      {label}
                    </MenuItem>
                  )}
                </InternalLink>
              ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="border-primary border-t" />

        {spkTokenSubLinks.length === 1 && (
          <TopbarButton
            to={spkTokenSubLinks[0]!.link.to}
            label={spkTokenSubLinks[0]!.standaloneLabel}
            prefixIcon={LINKS_DATA.sparkToken.icon}
            type="sparkToken"
            onClick={closeDialog}
          />
        )}
        {spkTokenSubLinks.length > 1 && (
          <Accordion type="single" collapsible defaultValue={isSparkTokenSubLinkActive ? 'nav-accordion' : ''}>
            <AccordionItem value="nav-accordion">
              <AccordionTrigger className="w-full p-0 pr-5" role="none">
                <TopbarButton
                  label={LINKS_DATA.sparkToken.label}
                  type="sparkToken"
                  prefixIcon={LINKS_DATA.sparkToken.icon}
                  active={isSparkTokenSubLinkActive}
                  className="w-full"
                />
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-2 px-12 pb-4">
                {spkTokenSubLinks.map(({ link, label, apy }) => (
                  <InternalLink key={link.to} {...link}>
                    {({ isActive }) => (
                      <MenuItem
                        onClick={closeDialog}
                        className="overflow-hidden border border-primary p-0 text-secondary"
                      >
                        <SparkTokenNavLinkContent isActive={isActive}>
                          <div className="typography-label-2 text-primary">{label}</div>
                          {apy && (
                            <div className="typography-label-3 rounded-full bg-gradient-spark-primary-2 px-1.5 py-0.5 text-primary-inverse">
                              {formatPercentage(apy)}
                            </div>
                          )}
                        </SparkTokenNavLinkContent>
                      </MenuItem>
                    )}
                  </InternalLink>
                ))}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {import.meta.env.VITE_FEATURE_SWAP_PAGE === '1' && (
          <>
            <div className="border-primary border-t" />
            <TopbarButton
              to="/swap"
              label={LINKS_DATA.swap.label}
              prefixIcon={LINKS_DATA.swap.icon}
              type="swap"
              onClick={closeDialog}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
